import type { HpTagTone } from '@hp/ui';
import type { CatalogRow, CatalogScope, MarketCode } from './queries';

/** Scope filter of the rules tab, in toolbar order (labels: `leads:rules.scope.<scope>`). */
export const SCOPES = ['unruled', 'ruled', 'all'] as const satisfies readonly CatalogScope[];

/** A market code out of a select's string value, when it is one of the markets the API listed. */
export function marketFrom(value: string, known: ReadonlyArray<{ code: MarketCode }>): MarketCode | null {
  return known.find((m) => m.code === value)?.code ?? null;
}

/** What a campaign's platform status means for a person (labels: `leads:rules.platformStatus.<key>`). */
export const PLATFORM_STATUSES = ['running', 'paused', 'archived', 'deleted', 'other'] as const;
export type PlatformStatusKey = (typeof PLATFORM_STATUSES)[number];

/**
 * Meta `effective_status` (and Google's ENABLED/REMOVED) → a translated word. The API's "running only"
 * filter keeps exactly the `running` ones (ACTIVE / ENABLED, not deleted).
 */
export function platformStatusKey(status: string | null, isDeleted: boolean): PlatformStatusKey {
  const s = (status ?? '').toUpperCase();
  if (isDeleted || s === 'DELETED' || s === 'REMOVED') return 'deleted';
  if (s === 'ACTIVE' || s === 'ENABLED') return 'running';
  if (s === 'PAUSED' || s === 'CAMPAIGN_PAUSED' || s === 'ADSET_PAUSED') return 'paused';
  if (s === 'ARCHIVED') return 'archived';
  return 'other';
}

/** What a catalog row's status tag says (labels: `leads:rules.status.<status>`). */
export const RULE_STATUSES = ['no_rule', 'active', 'paused', 'owner_inactive'] as const;
export type RuleStatus = (typeof RULE_STATUSES)[number];

/** The rules tab's own tones: these statuses are not run statuses, so `statusTone()` does not know them. */
export const RULE_STATUS_TONE: Record<RuleStatus, HpTagTone> = {
  no_rule: 'warning',
  active: 'success',
  paused: 'neutral',
  owner_inactive: 'danger',
};

/**
 * A rule owner counts as inactive when the API says so or when the active-user list (once loaded)
 * does not contain them; the router skips such owners.
 */
export function isOwnerInactive(owner: { userPdId: number; active: boolean }, activeIds: ReadonlySet<number> | null) {
  return !owner.active || (activeIds !== null && !activeIds.has(owner.userPdId));
}

/** No rule → paused → an inactive owner → active (a paused rule routes nothing, so its owners matter less). */
export function ruleStatus(row: Pick<CatalogRow, 'rule'>, activeIds: ReadonlySet<number> | null): RuleStatus {
  const rule = row.rule;
  if (!rule) return 'no_rule';
  if (!rule.active) return 'paused';
  if (rule.owners.some((o) => isOwnerInactive(o, activeIds))) return 'owner_inactive';
  return 'active';
}

/** Fields `LEAD_RULE_INVALID` names in `details.field` that the editor has a label for (`leads:editor.field.<f>`). */
export const RULE_FIELDS = [
  'adCampaignId',
  'ownerUserPdIds',
  'pipelinePdId',
  'stagePdId',
  'campaignOption',
  'projectOptionId',
] as const;
export type RuleField = (typeof RULE_FIELDS)[number];

export function isRuleField(value: unknown): value is RuleField {
  return typeof value === 'string' && (RULE_FIELDS as readonly string[]).includes(value);
}

// --- tab "General rules" ---

/** Statuses a lead can have (labels: `leads:general.leadStatus.<status>`); the API's `LEAD_STATUSES`. */
export const LEAD_STATUSES = [
  'received',
  'routed',
  'delivering',
  'delivered',
  'duplicate',
  'needs_rule',
  'failed',
  'invalid',
] as const;
export type LeadStatusKey = (typeof LEAD_STATUSES)[number];

export function isLeadStatus(value: unknown): value is LeadStatusKey {
  return typeof value === 'string' && (LEAD_STATUSES as readonly string[]).includes(value);
}

/** Lead statuses are not run statuses, so `statusTone()` does not know them. */
export const LEAD_STATUS_TONE: Record<LeadStatusKey, HpTagTone> = {
  received: 'neutral',
  routed: 'info',
  delivering: 'info',
  delivered: 'success',
  duplicate: 'neutral',
  needs_rule: 'warning',
  failed: 'danger',
  invalid: 'danger',
};

/** Where a routed lead's owner came from (labels: `leads:general.ownerSource.<source>`). */
export const OWNER_SOURCES = ['existing_owner', 'rule_round_robin', 'market_round_robin', 'fallback'] as const;
export type OwnerSourceKey = (typeof OWNER_SOURCES)[number];

export function isOwnerSource(value: unknown): value is OwnerSourceKey {
  return typeof value === 'string' && (OWNER_SOURCES as readonly string[]).includes(value);
}

/** Why the inbound endpoint refused a body (labels: `leads:general.channels.rejectReason.<reason>`). */
export const REJECT_REASONS = ['empty_body', 'unparsable_body', 'not_an_object', 'missing_leadgen_id'] as const;
export type RejectReason = (typeof REJECT_REASONS)[number];

export function isRejectReason(value: unknown): value is RejectReason {
  return typeof value === 'string' && (REJECT_REASONS as readonly string[]).includes(value);
}

/** A market default can take a live channel's leads only with a pipeline, a stage and an active owner. */
export function isDefaultComplete(
  rule: { pipeline: unknown; stage: unknown; owners: ReadonlyArray<{ userPdId: number; active: boolean }> },
  activeIds: ReadonlySet<number> | null,
): boolean {
  return rule.pipeline !== null && rule.stage !== null && rule.owners.some((o) => !isOwnerInactive(o, activeIds));
}

/** Settings fields `LEAD_SETTINGS_INVALID` names in `details.field` (labels: `leads:general.settings.field.<f>`). */
export const SETTINGS_FIELDS = [
  'preferExistingOwner',
  'fallbackOwnerUserPdId',
  'createActivity',
  'activityTemplate',
] as const;
export type SettingsField = (typeof SETTINGS_FIELDS)[number];

export function isSettingsField(value: unknown): value is SettingsField {
  return typeof value === 'string' && (SETTINGS_FIELDS as readonly string[]).includes(value);
}

// --- tab "Received leads" ---

/** The status filter of the received tab, in toolbar order (labels: `leads:received.filter.<key>`). */
export const RECEIVED_FILTERS = ['exceptions', 'all', 'delivered', 'needs_rule', 'failed', 'invalid'] as const;
export type ReceivedFilter = (typeof RECEIVED_FILTERS)[number];

/** Period select of the received tab (labels: `leads:received.period.<key>`); `30d` is the API's own default. */
export const RECEIVED_PERIODS = ['24h', '7d', '30d'] as const;
export type ReceivedPeriod = (typeof RECEIVED_PERIODS)[number];
export const PERIOD_HOURS: Record<ReceivedPeriod, number> = { '24h': 24, '7d': 7 * 24, '30d': 30 * 24 };

export function isReceivedPeriod(value: unknown): value is ReceivedPeriod {
  return typeof value === 'string' && (RECEIVED_PERIODS as readonly string[]).includes(value);
}

/** Statuses a lead is still moving through on its own: its detail polls until it settles. */
export const IN_FLIGHT_STATUSES: readonly LeadStatusKey[] = ['received', 'routed', 'delivering'];

/** Trace step names in cascade order (labels: `leads:received.trace.step.<step>`); the engine's TRACE_STEPS. */
export const TRACE_STEPS = ['duplicate', 'campaign', 'rule', 'target', 'person', 'owner', 'decision'] as const;
export type TraceStepKey = (typeof TRACE_STEPS)[number];

export function isTraceStep(value: unknown): value is TraceStepKey {
  return typeof value === 'string' && (TRACE_STEPS as readonly string[]).includes(value);
}

/** Every result the engine writes into a trace step (labels: `leads:received.trace.result.<result>`). */
export const TRACE_RESULTS = [
  'new',
  'duplicate',
  'matched',
  'unknown',
  'campaign_rule',
  'campaign_rule_inactive_market_default',
  'market_default',
  'no_market_default',
  'incomplete',
  'set',
  'none',
  'existing_owner',
  'rule_round_robin',
  'market_round_robin',
  'fallback',
  'routed',
] as const;
export type TraceResultKey = (typeof TRACE_RESULTS)[number];

export function isTraceResult(value: unknown): value is TraceResultKey {
  return typeof value === 'string' && (TRACE_RESULTS as readonly string[]).includes(value);
}

/** Why a lead waits for a rule (labels: `leads:received.needsRuleReason.<reason>`); the engine's NEEDS_RULE_REASONS. */
export const NEEDS_RULE_REASONS = [
  'no_market_default',
  'market_default_incomplete',
  'rule_incomplete',
  'no_owner',
] as const;
export type NeedsRuleReason = (typeof NEEDS_RULE_REASONS)[number];

export function isNeedsRuleReason(value: unknown): value is NeedsRuleReason {
  return typeof value === 'string' && (NEEDS_RULE_REASONS as readonly string[]).includes(value);
}

/** Why a lead was dropped as invalid (labels: `leads:received.invalidReason.<reason>`). */
export const INVALID_REASONS = [
  'no_contact',
  'not_an_object',
  'unknown_channel',
  'missing_leadgen_id',
  'meta_lead_not_found',
  'meta_not_configured',
] as const;
export type InvalidReason = (typeof INVALID_REASONS)[number];

export function isInvalidReason(value: unknown): value is InvalidReason {
  return typeof value === 'string' && (INVALID_REASONS as readonly string[]).includes(value);
}

/** What a dry run's outcome tag says (labels: `leads:received.outcome.<kind>`), with its tone. */
export const OUTCOME_TONE: Record<'routed' | 'needs_rule' | 'duplicate', HpTagTone> = {
  routed: 'success',
  needs_rule: 'warning',
  duplicate: 'neutral',
};

/** "12 s" / "3 min" / "2 h" for a delivery time in whole seconds. */
export function durationParts(seconds: number): { unit: 's' | 'min' | 'h'; value: number } {
  if (seconds < 60) return { unit: 's', value: seconds };
  if (seconds < 3600) return { unit: 'min', value: Math.round(seconds / 60) };
  return { unit: 'h', value: Math.round(seconds / 360) / 10 };
}

/** What a manager can do with a received lead from the table or the detail; the tab confirms and runs it. */
export type LeadAction = 'retry' | 'reroute';

/** The action a lead's status allows: retry a failed delivery, re-route a lead that waits for a rule. */
export function actionFor(status: string): LeadAction | null {
  if (status === 'failed') return 'retry';
  if (status === 'needs_rule') return 'reroute';
  return null;
}
